// DepthReadout — on-device measurement HUD for the depth-sensing spike
// (?depthHud / ?depthPlace). Bottom-left monospace pill showing:
//   D — center-pixel depth-map distance (the car surface)
//   P — plane hit-test distance (the detected plane behind it)
//   Δ — P − D in centimetres: how far behind the real surface the
//       plane path would have placed the wheel. THE number for the
//       Z-plane precision work.
// Subscribes to the driver's plane store (which fires on every
// sessionInfo write) via useSyncExternalStore, same pattern as
// PlaneDiagnosticsHUD.

import { useCallback, useMemo, useSyncExternalStore } from 'react'
import type { DepthStatus, DetectedPlanesStore } from '@/ar/webxr-driver'

interface DepthReadoutProps {
  store: DetectedPlanesStore
  /** Read fresh each render — the driver rewrites sessionInfo in place
   *  and fires the store listeners when it changes. */
  getDepthStatus: () => DepthStatus
}

function meters(v: number | null): string {
  return v == null ? '—' : v.toFixed(2)
}

function deltaCm(v: number | null): string {
  if (v == null) return '—'
  const cm = v * 100
  return `${cm >= 0 ? '+' : ''}${cm.toFixed(0)}`
}

export function DepthReadout({ store, getDepthStatus }: DepthReadoutProps) {
  const subscribe = useMemo(
    () => (cb: () => void) => store.subscribe(() => cb()),
    [store],
  )
  const getSnapshot = useCallback(() => store.getState(), [store])
  useSyncExternalStore(subscribe, getSnapshot)
  const status = getDepthStatus()

  const panelStyle: React.CSSProperties = {
    position: 'fixed',
    bottom: 18,
    left: 6,
    padding: '6px 9px',
    background: 'rgba(0, 0, 0, 0.78)',
    color: 'white',
    font: '11px/1.4 ui-monospace, "SF Mono", Menlo, monospace',
    borderRadius: 8,
    pointerEvents: 'none',
    zIndex: 100,
  }

  if (!status.featureGranted) {
    return (
      <div data-testid="depth-readout-not-granted" style={panelStyle}>
        <span style={{ color: '#ffb6b6' }}>
          depth-sensing not granted
          {status.requested ? '' : ' (not requested)'}
        </span>
      </div>
    )
  }

  // Delta colour: green when the two sensors agree (≤5 cm — plane
  // placement would be fine), amber past that (the depth path is
  // actively correcting a plane-behind-the-car error).
  const delta = status.lastDeltaM
  const deltaColor =
    delta != null && Math.abs(delta) > 0.05 ? '#ffe27a' : '#73ff8b'

  return (
    <div data-testid="depth-readout" style={panelStyle}>
      <span>D {meters(status.lastDepthM)}m</span>
      {' · '}
      <span>P {meters(status.lastPlaneHitDistanceM)}m</span>
      {' · '}
      <span style={{ color: deltaColor }}>Δ {deltaCm(delta)}cm</span>
      {' · '}
      <span style={{ opacity: 0.7 }}>
        {status.framesWithDepth}/{status.framesWithDepth + status.framesWithoutDepth}
      </span>
      {/* Second line: what the runtime granted + why the last sample
          attempt failed. The on-device triage line — a gpu-optimized
          grant or a chronic info-null tells the whole story without
          USB debugging. */}
      <div style={{ opacity: 0.75 }}>
        {status.depthUsage ?? 'usage?'} · {status.depthDataFormat ?? 'format?'}
        {status.lastFailure != null && (
          <span style={{ color: '#ffb6b6' }}> · {status.lastFailure}</span>
        )}
      </div>
    </div>
  )
}
